/**
 * Production-ready logging utility for debugging and monitoring
 */

interface LogLevel {
  name: 'info' | 'warn' | 'error' | 'debug';
  color: string;
}

const LOG_LEVELS: Record<string, LogLevel> = {
  info: { name: 'info', color: '#3B82F6' },
  warn: { name: 'warn', color: '#F59E0B' },
  error: { name: 'error', color: '#EF4444' },
  debug: { name: 'debug', color: '#8B5CF6' },
};

const isDevelopment = import.meta.env.MODE === 'development';

/**
 * Send error to monitoring service (Sentry, LogRocket, etc.)
 * Configure based on your monitoring setup
 */
function sendToMonitoring(level: string, message: string, error?: unknown, context?: Record<string, unknown>) {
  // Only send errors and warnings to monitoring in production
  if (isDevelopment) return;

  if (level !== 'error' && level !== 'warn') return;

  try {
    // TODO: Configure your monitoring service here
    // Example: Sentry.captureException(error, { level, extra: context })
    
    // For now, we'll send to a logging endpoint
    const logData = {
      level,
      message,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      context,
      errorStack: error instanceof Error ? error.stack : undefined,
    };

    // Send to your backend logging endpoint
    // fetch('/api/logs', { method: 'POST', body: JSON.stringify(logData) }).catch(() => {});
  } catch (err) {
    // Fail silently to avoid infinite error loops
    console.error('Failed to send log to monitoring:', err);
  }
}

function formatMessage(message: string, context?: Record<string, unknown>): string {
  if (!context || Object.keys(context).length === 0) return message;
  return `${message} | ${JSON.stringify(context)}`;
}

export const logger = {
  info: (message: string, context?: Record<string, unknown>) => {
    const logLevel = LOG_LEVELS.info;
    const formatted = formatMessage(message, context);
    if (isDevelopment) {
      console.log(`%c[${logLevel.name.toUpperCase()}]`, `color: ${logLevel.color}; font-weight: bold;`, formatted);
    }
  },

  warn: (message: string, context?: Record<string, unknown>) => {
    const logLevel = LOG_LEVELS.warn;
    const formatted = formatMessage(message, context);
    if (isDevelopment) {
      console.warn(`%c[${logLevel.name.toUpperCase()}]`, `color: ${logLevel.color}; font-weight: bold;`, formatted);
    }
    sendToMonitoring(logLevel.name, message, undefined, context);
  },

  error: (message: string, error?: unknown, context?: Record<string, unknown>) => {
    const logLevel = LOG_LEVELS.error;
    const formatted = formatMessage(message, context);
    if (isDevelopment) {
      console.error(`%c[${logLevel.name.toUpperCase()}]`, `color: ${logLevel.color}; font-weight: bold;`, formatted);
      if (error) console.error(error);
    }
    sendToMonitoring(logLevel.name, message, error, context);
  },

  debug: (message: string, context?: Record<string, unknown>) => {
    if (!isDevelopment) return;
    const logLevel = LOG_LEVELS.debug;
    const formatted = formatMessage(message, context);
    console.log(`%c[${logLevel.name.toUpperCase()}]`, `color: ${logLevel.color}; font-weight: bold;`, formatted);
  },
};
